import React, { FC, memo, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Grid } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { resolver } from './resolver'
import { Modal } from '../../../../../../components/Dashboard/Modal'
import { InputField } from '../../../../../../components/Dashboard/InputField'
import { InputSelect } from '../../../../../../components/Dashboard/InputSelect'
import { CURRENCIES } from '../../../../../../constants/currencies'
import { Loader } from '../../../../../../components/Loader'
import { useRefreshTransactions, useRest } from '../../../../../../components/Context'

const useStyles = makeStyles((theme: Theme) => ({
  body: {
    width: 345,
    paddingTop: theme.spacing(1),
    flexWrap: 'wrap',
  },
  input: {
    height: 88,
    '& label': {
      paddingBottom: theme.spacing(0.5),
    },
  },
}))

interface ITransferToStakingPopup {
  isOpen: boolean
  modalClose: () => void
  title?: string
}

type Input = {
  currency: string
  amount: number
  addressFrom: string
  addressTo: string
}

interface IIdentifier {
  address: string
  identifier: string
}

type SelectedSelect = {
  value: string
  label: string
}

const TransferPopup: FC<ITransferToStakingPopup> = ({ isOpen, modalClose, title }) => {
  const formMethods = useForm<Input>({
    mode: 'onBlur',
    resolver,
  })
  const classes = useStyles()
  const { rxiosAuth } = useRest()
  // eslint-disable-next-line
  const [assetId, setAssetId] = useState<number | undefined>()
  const [loading, setLoading] = useState<boolean>(false)
  const { saveIsRefreshTransactions } = useRefreshTransactions()
  const [selectedWallet, setSelectedWallet] = useState<SelectedSelect[]>()
  // eslint-disable-next-line
  const [selectedAsset, setSelectedAsset] = useState<SelectedSelect[]>()
  const { handleSubmit, reset } = formMethods

  const onSubmit = useCallback(
    async (data: Input) => {
      setLoading(true)

      rxiosAuth.post(``, data).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          setLoading(false)
        },
        (error) => {
          toast.error(error.response.data.message)
          setLoading(false)
        },
        () => {
          setLoading(false)
          toast.success('Staking has been transfered')
          modalClose()
          saveIsRefreshTransactions(true)
          formMethods.reset()
        },
      )
    },
    // eslint-disable-next-line
    [assetId, rxiosAuth],
  )

  const getSelectedAsset = useCallback(() => {
    setLoading(true)
    rxiosAuth.get(`settlement/get-addresses`).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        const { status, data } = next
        if (status === 'success') {
          const identifierArr = []
          const identifierApproved: IIdentifier[] = data.filter(
            (item: { statusSA: string }) => item.statusSA === 'approved',
          )

          // eslint-disable-next-line guard-for-in,no-restricted-syntax
          for (const item in identifierApproved) {
            identifierArr.push({
              value: identifierApproved[item]?.address,
              label: identifierApproved[item]?.identifier,
            } as SelectedSelect)
          }
          setSelectedAsset(identifierArr)
        }
        setLoading(false)
      },
      (error) => {
        setLoading(false)
        toast.error(error.response.data.message)
      },
    )
  }, [rxiosAuth])

  const getSelectedWallets = useCallback(
    () => {
      setLoading(true)

      rxiosAuth.get('trustology/user/wallets?page=1&count=1000').subscribe(
        // eslint-disable-next-line
        (next: any) => {
          const { items } = next
          if (items.length > 0) {
            const nameArr = []
            // eslint-disable-next-line guard-for-in,no-restricted-syntax
            for (const item of items) {
              nameArr.push({
                value: item.address,
                label: item.name,
              } as SelectedSelect)
            }
            setSelectedWallet(nameArr)
          }
          setLoading(false)
        },
        (error) => {
          setLoading(false)
          toast.error(error.response.data.message)
        },
      )
    },
    // eslint-disable-next-line
    [rxiosAuth],
  )

  useEffect(() => {
    if (!isOpen) {
      setLoading(false)
      setTimeout(reset, 300)
    } else {
      getSelectedAsset()
      getSelectedWallets()
    }
  }, [getSelectedWallets, getSelectedAsset, isOpen, reset])

  return (
    <Modal
      title={title || ''}
      isOpen={isOpen}
      modalClose={modalClose}
      isSubmit
      onSubmit={handleSubmit(onSubmit)}
      submitTitle={'Send'}
      isWidthAuto
    >
      {loading && <Loader />}
      <FormProvider {...formMethods}>
        <Grid container spacing={3} className={classes.body}>
          <Grid item xs={12} className={classes.input}>
            <InputSelect
              inputName="asset"
              labelName={'Asset'}
              width={320}
              enums={CURRENCIES}
              placeholder={'Please select'}
            />
          </Grid>

          <Grid item xs={12} className={classes.input}>
            <InputSelect
              inputName="wallet"
              labelName={'Wallet'}
              width={320}
              placeholder={'Please select'}
              enums={selectedWallet}
            />
          </Grid>

          <Grid item xs={12} className={classes.input}>
            <InputField inputName="amount" labelName={'Amount'} inputType="text" width={320} />
          </Grid>
        </Grid>
      </FormProvider>
    </Modal>
  )
}

export const MemoTransferPopup = memo(TransferPopup)
